import { defineComponent as _defineComponent } from 'vue'
import { useFileLayers } from '@/composables/externalFileParser';
import store from '@/store';
import { computed, onMounted, ref, watch } from 'vue';
import { TreeItem, TreeType } from '@/components/Layers/modal/types';
import ModalImportSearch from '@/components/Layers/modal/import/search/ModalImportSearch.vue';
import ModalImportLayersTree from '@/components/Layers/modal/import/layers-tree/ModalImportLayersTree.vue';
import CSelect from '@/components/shared/CSelect/CSelect.vue';
import type { CSelectItem, Primitive } from '@/components/shared/CSelect';


export default /*#__PURE__*/_defineComponent({
  __name: 'AmazonImportTabsModal',
  setup(__props) {

const showLoader = ref(false);
const loaderMessage = ref('Loading...');
const searchText = ref('');
const perPage = 8;
const currentPage = ref(1);

const fileBuffer = computed(() => store.state.storeLayersModal?.fileBuffer);
const { data: amazonLayers, parseAndExtractLayers } = useFileLayers('AMAZON_FILE_XLSX');

const amazonHeader = computed(() => ({
    id: '',
    name: amazonLayers.value?.artworkName || 'Amazon Template',
    mode: amazonLayers.value?.mode
}));

const surfaces = computed<CSelectItem<number>[]>(
    () =>
        amazonLayers.value?.pages.map((page) => ({
            label: `${page.name}`,
            value: page.pageNumber
        })) || []
);

const selectedSurface = ref<CSelectItem<number>>(surfaces.value[0]);

const selectedPage = computed(() => selectedSurface.value?.value || 1);

const page = computed(() => amazonLayers.value?.pages.find((page) => page.pageNumber === selectedPage.value) || null);

watch(selectedPage, (newPage) => {
    if (!amazonLayers.value || !newPage) return;
    amazonLayers.value.selectedPage = newPage;
    // Update the store when page changes
    store.dispatch('storeLayersModal/setParsedFileLayers', amazonLayers.value);
});

const treeItemChildren = computed(() =>
    page.value
        ? page.value.elements.map(
              (element, index): TreeItem => ({
                  id: index + 1,
                  name: element.name,
                  icon: '',
                  type: element.type as TreeType,
                  metadata: element
              })
          )
        : []
);

const treeItemElements = computed(() => {
    const startIndex = (currentPage.value - 1) * perPage;
    const endIndex = startIndex + perPage;

    const childItems: TreeItem = {
        name: 'Select All',
        icon: '',
        type: TreeType.FROM_FILE_ELEMENT,
        paginatedChildren: treeItemChildren.value.slice(startIndex, endIndex) || [],
        children: treeItemChildren.value || []
    };
    return childItems;
});

const filteredTreeItems = computed(() => {
    const startIndex = (currentPage.value - 1) * perPage;
    const endIndex = startIndex + perPage;
    const { children: childrenTreeItems } = treeItemElements.value;

    if (!childrenTreeItems || !searchText.value) {
        return treeItemElements.value;
    }

    const filteredTreeItems = [...childrenTreeItems].filter((childTreeType) => {
        if (!childTreeType.name) return false;
        return childTreeType.name.toLowerCase().includes(searchText.value.toLowerCase());
    });

    const result = {
        ...treeItemElements.value,
        children: filteredTreeItems,
        paginatedChildren: filteredTreeItems.slice(startIndex, endIndex) || []
    };
    return result;
});

const pageElementsCount = computed(() => filteredTreeItems.value.children?.length || 0);

const templateStats = computed(() => {
    if (!page.value) {
        return { components: 0, canvasObjects: 0, bindings: 0 };
    }

    // Count elements by type
    const elements = page.value.elements || [];
    const canvasObjects = elements.filter(
        (el) => el.type === TreeType.FROM_FILE_TEXT_ELEMENT || el.type === TreeType.FROM_FILE_IMAGE_ELEMENT
    ).length;
    const components = elements.length - canvasObjects;

    return {
        components,
        canvasObjects,
        bindings: 0 // Will be calculated from customData if available
    };
});

onMounted(async () => {
    if (amazonLayers.value) {
        showLoader.value = false;
        return;
    }
    await parseAmazonFile();
});

const parseAmazonFile = async () => {
    try {
        showLoader.value = true;
        loaderMessage.value = 'Loading Amazon template...';
        await parseAndExtractLayers(fileBuffer.value!);

        // Store the parsed layers in the store for the import modal to access
        if (amazonLayers.value) {
            store.dispatch('storeLayersModal/setParsedFileLayers', amazonLayers.value);
        }

        showLoader.value = false;
    } catch (error) {
        console.error('Error parsing Amazon file:', error);
        showLoader.value = false;
    }
};

const updateSearchText = (value: string) => {
    searchText.value = value;
    currentPage.value = 1;
};

const onSurfaceChange = (newSurface?: CSelectItem<Primitive> | Primitive) => {
    if (!newSurface) return;
    if (typeof newSurface === 'object') {
        selectedSurface.value = newSurface as CSelectItem<number>;
    } else if (typeof newSurface === 'number') {
        const surface = surfaces.value.find((s) => s.value === newSurface);
        if (surface) {
            selectedSurface.value = surface;
        }
    }
};

watch(
    () => surfaces.value,
    (newSurfaces) => {
        if (newSurfaces.length > 0 && !selectedSurface.value) {
            selectedSurface.value = newSurfaces[0];
        }
    }
);

return { __sfc: true,showLoader, loaderMessage, searchText, perPage, currentPage, fileBuffer, amazonLayers, parseAndExtractLayers, amazonHeader, surfaces, selectedSurface, selectedPage, page, treeItemChildren, treeItemElements, filteredTreeItems, pageElementsCount, templateStats, parseAmazonFile, updateSearchText, onSurfaceChange, ModalImportSearch, ModalImportLayersTree, CSelect }
}

})